import { useCallback, useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { Flex, View } from '@adobe/react-spectrum';
import { DraftIcon, TrashIcon, MyMessagesIcon, SentIcon, FolderIcon } from '../../../components/protected/Icons/IconsLib';
import moment from 'moment';
import { useDependency } from '../../../contexts/DependencyProvider';
import { useTranslation } from 'react-i18next';
import CommunicationList from './component/CommunicationList';
import { useMessageCount } from '../../../contexts/MessageCountContext';
import { Loader } from '@progress/kendo-react-indicators';
import styles from './communication_menu.module.css';

interface ICommunicationMenuProps {
  showMenu?: boolean;
  setShowMobileMenu: (show: boolean) => void;
  setIsCommMenuOpen?: (isOpen: boolean) => void;
}

const CommunicationMenu: React.FC<ICommunicationMenuProps> = ({ showMenu = false, setShowMobileMenu, setIsCommMenuOpen }) => {
  const { t } = useTranslation();
  const { store } = useDependency();
  const [show, setShow] = useState(false);
  const [selectedRecordView, setSelectedRecordView] = useState<'todo' | 'messages' | 'sent' | 'trash' | 'corporate' | 'concept' | ''>('todo');
  const { totalUnreadMessage, totalUnreadTodo, isLoading } = useMessageCount();

  const handleClose = () => {
    setShow(false);
    setShowMobileMenu(true);
  };

  const handleShow = () => {
    setShow(true);
  };

  const createParams = useCallback(async (filterTypeName: any, filterValue: any) => {
    //initializeFilterParams();
    const filterValueStr = filterTypeName === 'state' ? filterValue : Array.from(filterValue).join(',');
    handleFilterType(filterTypeName, filterValueStr, filterValue);
  }, []);

  const handleFilterType = (filterTypeName: any, filterValueStr: any, filterValue: any) => {
    //console.log('filterTypeName', filterTypeName);
    switch (filterTypeName) {
      case 'type':
        updateFilterParams('projectType', 'equals', filterValueStr === 'All types' ? '' : filterValueStr);
        break;
      case 'state':
        updateFilterParams('state', 'in', filterValue === 'All records' ? '' : filterValue);
        break;
      case 'period':
        updateFilterParams('finalDate', filterValue, filterValue === 'Any period' ? '' : filterValue);
        break;
      case 'customState':
        updateFilterParams('customState', 'in', filterValueStr === 'All states' ? '' : filterValueStr);
        break;
      case 'user':
        updateFilterParams('ownerId', 'equals', filterValueStr === 'All users' ? '' : filterValueStr);
        break;
      default:
        break;
    }
  };

  const updateFilterParams = (name: string, type: string, value: string) => {
    let ownerObj = {};
    let greaterObj = {};

    if (name === 'state') {
      ({ value, ownerObj } = handleStateFilter(name, value, ownerObj));
    }

    if (name === 'finalDate') {
      ({ value, greaterObj } = handleFinalDateFilter(name, type, value, greaterObj));
    }
  };

  const handleStateFilter = (name: string, value: string, ownerObj: any) => {
    if (value === 'todo') {
      value = '1,2';
      ownerObj = { name: 'ownerId', type: 'equals', value: store.UserId };
    }
    return { value, ownerObj };
  };

  const handleFinalDateFilter = (name: string, type: string, value: string, greaterObj: any) => {
    if (type === 'less') {
      value = moment().format('YYYY-MM-DDTHH:MM:ss');
    } else if (type === 'lessOrEquals') {
      value = moment().add(3, 'months').format('YYYY-MM-DDT00:00:00');
      greaterObj = { name: 'finalDate', type: 'greaterOrEquals', value: moment().format('YYYY-MM-DDT00:00:00') };
    } else if (type === 'greater') {
      value = moment().format('YYYY-MM-DDT00:00:00');
    }
    return { value, greaterObj };
  };

  //create component of following structure
  const circleCounter = (totalCount: number) => (
    <Flex width={'28px'} direction={'column'} position={'static'} UNSAFE_className={styles.counter_circle} alignItems={'center'} justifyContent={'center'}>
      {totalCount}
    </Flex>
  );

  return (
    <>
      <Offcanvas show={show} onHide={handleClose} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title></Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Flex
            direction={'column'}
            position={'absolute'}
            top={'50px'}
            left={'15px'}
            width={'95%'}
            alignItems={'start'}
            justifyContent={'start'}
            gap={'5px'}
            UNSAFE_className={styles.views_menu_container}
          >
            <Flex direction={'row'} UNSAFE_className={styles.views_menu_heading} alignSelf={'stretch'} alignItems={'center'} justifyContent={'start'}>
              <View position={'relative'} UNSAFE_className={styles.menu_heading_text}>
                {t('views', { ns: 'layout_components' })}
              </View>
            </Flex>
            <Flex direction={'column'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} gap={'4px'} marginBottom={'0px'} UNSAFE_className={styles.message_views_parent}>
              <a
                onClick={() => {
                  setSelectedRecordView('todo');
                  createParams('state', 'todo');
                  handleClose();
                }}
              >
                <Flex
                  direction={'row'}
                  height={'50px'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  UNSAFE_className={selectedRecordView === 'todo' ? styles.message_views_container_selected : styles.message_views_container}
                >
                  <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'} marginStart={'3px'}>
                    <Flex position={'relative'} flex={1} UNSAFE_className={styles.message_views_text} gap={'18px'} UNSAFE_style={{ fontSize: '18px' }}>
                      <i className="bi bi-check-square" style={{ color: '#B0BEC5' }} /> {t('Todo', { ns: 'layout_components' })}
                    </Flex>
                    {isLoading ? <Loader type="infinite-spinner" size="small" /> : totalUnreadTodo > 0 && circleCounter(totalUnreadTodo)}
                  </Flex>
                </Flex>
              </a>

              <a
                onClick={() => {
                  setSelectedRecordView('messages');
                  createParams('state', 'messages');
                  handleClose();
                }}
              >
                <Flex
                  direction={'row'}
                  height={'50px'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  UNSAFE_className={selectedRecordView === 'messages' ? styles.message_views_container_selected : styles.message_views_container}
                >
                  <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                    <Flex position={'relative'} flex={1} UNSAFE_className={styles.message_views_text} gap={'12px'} UNSAFE_style={{ fontSize: '18px' }}>
                      <MyMessagesIcon size={24} />
                      {t('Messages', { ns: 'layout_components' })}
                    </Flex>
                    {isLoading ? <Loader type="infinite-spinner" size="small" /> : totalUnreadMessage > 0 && circleCounter(totalUnreadMessage)}
                  </Flex>
                </Flex>
              </a>

              <a
                onClick={() => {
                  setSelectedRecordView('sent');
                  createParams('state', 'sent');
                  handleClose();
                }}
              >
                <Flex
                  direction={'row'}
                  height={'50px'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  UNSAFE_className={selectedRecordView === 'sent' ? styles.message_views_container_selected : styles.message_views_container}
                >
                  <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                    <Flex position={'relative'} flex={1} UNSAFE_className={styles.message_views_text} gap={'12px'} UNSAFE_style={{ fontSize: '18px' }}>
                      <SentIcon size={24} />
                      {t('Sent', { ns: 'layout_components' })}
                    </Flex>
                  </Flex>
                </Flex>
              </a>

              <a
                onClick={() => {
                  setSelectedRecordView('concept');
                  createParams('state', 'concept');
                  handleClose();
                }}
              >
                <Flex
                  direction={'row'}
                  height={'50px'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  UNSAFE_className={selectedRecordView === 'concept' ? styles.message_views_container_selected : styles.message_views_container}
                >
                  <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                    <Flex position={'relative'} flex={1} UNSAFE_className={styles.message_views_text} gap={'12px'} UNSAFE_style={{ fontSize: '18px' }}>
                      <DraftIcon size={24} />
                      {t('Concept', { ns: 'layout_components' })}
                    </Flex>
                  </Flex>
                </Flex>
              </a>

              <a
                onClick={() => {
                  setSelectedRecordView('trash');
                  createParams('state', 'trash');
                  handleClose();
                }}
              >
                <Flex
                  direction={'row'}
                  height={'50px'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  UNSAFE_className={selectedRecordView === 'trash' ? styles.message_views_container_selected : styles.message_views_container}
                >
                  <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                    <Flex position={'relative'} flex={1} UNSAFE_className={`${styles.message_views_text} ${styles.trash_icon}`} gap={'12px'} UNSAFE_style={{ fontSize: '18px' }}>
                      <TrashIcon size={24} />
                      {t('Trash', { ns: 'layout_components' })}
                    </Flex>
                  </Flex>
                </Flex>
              </a>

              <a
                onClick={() => {
                  setSelectedRecordView('corporate');
                  createParams('state', 'corporate');
                  handleClose();
                }}
              >
                <Flex
                  direction={'row'}
                  height={'50px'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  UNSAFE_className={selectedRecordView === 'corporate' ? styles.message_views_container_selected : styles.message_views_container}
                >
                  <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                    <Flex position={'relative'} flex={1} UNSAFE_className={styles.message_views_text} gap={'12px'} UNSAFE_style={{ fontSize: '18px' }}>
                      <FolderIcon size={24} />
                      {t('Corporate Messages', { ns: 'layout_components' })}
                    </Flex>
                  </Flex>
                </Flex>
              </a>
            </Flex>
          </Flex>
        </Offcanvas.Body>
      </Offcanvas>

      <View UNSAFE_className={styles.mobileAppScreen}>
        <CommunicationList setShow={setShow} setSelectedRecordView={setSelectedRecordView} setShowMobileMenu={setShowMobileMenu} messageType={selectedRecordView} />
      </View>
    </>
  );
};

export default CommunicationMenu;
